import { bind } from '@react-rxjs/core';
import { createSignal, mergeWithKey } from '@react-rxjs/utils';
import dayjs from 'dayjs';
import mapValues from 'lodash.mapvalues';
import {
  concat,
  defer,
  filter,
  from,
  map,
  merge,
  NEVER,
  Observable,
  of,
  skip,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { queueNotice } from '../components/Notice';
import { imageUrlToFile } from '../components/SignatureInput';
import { navigate } from '../components/useProgramaticNavigator';
import { languageChanged$ } from '../i18n';
import {
  convertFromNminTimeline,
  convertToNminTimeline,
  minutesToTimeString,
} from '../pages/Logbook/utils';
import {
  DriverStatus,
  DriverStatusResponse,
  GeneratedLogbook,
  Logbook,
  LogbookFormState,
  LogbookIntermediateFormState,
  LogbookListItem,
  LogbookSignatureReposponse,
} from '../types/logbook';
import { downloadFileUrl } from '../utils/downloadFile';
import { stateFromUrl } from '../utils/rxjs/stateFromUrl';
import { convertToAsyncTaskState, onlyResult } from '../utils/rxjs/task';
import { driver$ } from './driver';
import { isRnWrapper } from './reactNativeWrapper';
import { request } from './request';
import { downloadFileToRnWrapper } from '../utils/downloadFileToRnWrapper';
import { LOBOOK_EDIT_INTERVAL_MINUTES } from '../pages/Logbook/constants';

export const LOGBOOK_DATE_FORMAT = 'YYYY-MM-DD';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const [changeDriverStatus$, changeDriverStatus] =
  createSignal<DriverStatus>();
export const [useDriverStatusChange, driverStatusChange$] = bind(
  changeDriverStatus$.pipe(
    switchMap((status) =>
      request<DriverStatusResponse>(
        `/gateway/logbook-service/logbook/driver-status/${status}`,
        {
          method: 'POST',
        }
      ).pipe(
        map(({ status }) => status),
        convertToAsyncTaskState()
      )
    )
  ),
  { error: null, result: null, running: false }
);

export const [useCurrentDriverStatus] = bind(
  defer(() =>
    driverStatusChange$.pipe(
      skip(1),
      startWith(<null>null),
      switchMap(() =>
        request<DriverStatusResponse>(
          '/gateway/logbook-service/logbook/driver-status'
        ).pipe(map(({ status }) => status))
      )
    )
  )
);

export const [refreshLogbookList$, refreshLogbookList] = createSignal();

export const [useLogbookReportList, logbookReportList$] = bind(
  concat(of(null), refreshLogbookList$).pipe(
    switchMap(() =>
      request<LogbookListItem[]>(
        `/gateway/logbook-service/logbook/list?fromDate=${dayjs()
          .startOf('day')
          .subtract(56, 'days')
          .format(LOGBOOK_DATE_FORMAT)}&toDate=${dayjs()
          .startOf('day')
          .format(LOGBOOK_DATE_FORMAT)}`
      ).pipe(convertToAsyncTaskState())
    )
  )
);

export const [useLogbookReportListItem, logbookReportListItem$] = bind(
  stateFromUrl(['/logbook/download/:id']).pipe(
    map((res) => res?.params.id),
    filter(Boolean),
    map((id) => (isValidId(id) ? Number(id) : id)),
    switchMap((id) =>
      logbookReportList$.pipe(
        onlyResult(),
        map((list) =>
          list.find((item) => {
            if (typeof id === 'string') {
              return item.day === id;
            }

            return item?.id === id;
          })
        )
      )
    )
  )
);

export const [retryLogbookDownload$, retryLogbookDownload] = createSignal();
export const [useDownloadLogbookreport] = bind(
  logbookReportListItem$.pipe(
    switchMap((item) =>
      concat(of(null), retryLogbookDownload$).pipe(
        switchMap(() =>
          languageChanged$.pipe(
            tap((language) => {
              if (!item) {
                return;
              }

              let downloadUrl = '';
              const filename = `${item.day}.pdf`;

              if (item.status === 'DRAFT') {
                downloadUrl = `/logbook-draft/draft/${item.id}`;
              } else if (!item.status) {
                downloadUrl = `/logbook-draft/new?date=${item.day}&language=${language}&timezone=${timezone}`;
              } else if (item.status === 'SIGNED' && item.renderedFilename) {
                downloadUrl = `/gateway/logbook-service/logbook/file/${item.renderedFilename}`;
              }

              if (!downloadUrl) {
                return;
              }

              if (isRnWrapper()) {
                downloadFileToRnWrapper(
                  downloadUrl,
                  filename,
                  'logbook-pdf'
                ).catch(console.error);
              } else {
                downloadFileUrl(downloadUrl, filename);
              }
            })
          )
        )
      )
    )
  ),
  null
);

export const isValidId = (id: string) => !Number.isNaN(Number(id));

export const [useLogbookReport, logbookReport$] = bind<
  Logbook | GeneratedLogbook
>(
  defer(() =>
    stateFromUrl(['/logbook/report/:id']).pipe(
      map((res) => res?.params.id),
      filter(Boolean),
      switchMap((id) =>
        !isValidId(id)
          ? languageChanged$.pipe(
              switchMap((language) =>
                request<GeneratedLogbook>(
                  `/gateway/logbook-service/logbook/for-date?date=${id}&language=${language}&timezone=${timezone}`
                )
              )
            )
          : request<Logbook>(`/gateway/logbook-service/logbook/${id}`)
      )
    )
  )
);

export const LOGBOOK_MAX_PAGE = 3;
export const [setLogbookPage$, setLogbookPage] = createSignal<number>();
export const [useLogbookPage] = bind(setLogbookPage$, 1);

export const [setLogbookFormState$, setLogbookFormState] =
  createSignal<LogbookFormState>();
export const [useLogbookFormState, logbookFormState$] = bind(
  merge(
    setLogbookFormState$,
    logbookReport$.pipe(
      withLatestFrom(driver$),
      map(
        ([res, driver]): LogbookIntermediateFormState => ({
          day: res.day,
          timezone: res.timezone,
          vehicleName: res.vehicleName,
          language: res.language,
          time: res.time,
          endKm: res.endKm ?? 0,
          startKm: res.startKm ?? 0,
          distance: res.distance ?? 0,
          startAddress: res.startAddress ?? '',
          endAddress: res.endAddress ?? '',
          signature: res.signature ?? null,
          driverName:
            res.driverName ??
            `${driver?.firstName ?? ''} ${driver?.lastName ?? ''}`.trim(),
          notes: res.notes ?? '',
        })
      ),
      switchMap(
        (state): Observable<LogbookFormState> =>
          state.signature
            ? from(imageUrlToFile(state.signature, 'signature')).pipe(
                map((signature) => ({ ...state, signature }))
              )
            : of(state as LogbookFormState)
      )
    )
  )
);

export const [useInitialLogbookFormTimeline, useInitialLogbookFormTimeline$] =
  bind(
    logbookReport$.pipe(
      map(({ time }) =>
        convertToNminTimeline(time, LOBOOK_EDIT_INTERVAL_MINUTES)
      )
    )
  );

export const [useLogbookFormTimeline, logbookFormTimeline$] = bind(
  logbookFormState$.pipe(
    map(({ time }) => convertToNminTimeline(time, LOBOOK_EDIT_INTERVAL_MINUTES))
  )
);

export const [editLogbookTimeline$, editLogbookTimeline] =
  createSignal<DriverStatus[]>();
export const [saveEditedTimeline$, saveEditedTimeline] = createSignal();
export const [useLogbookEditedTimeline, editedTimeline$] = bind(
  logbookFormTimeline$.pipe(
    switchMap((initial) => editLogbookTimeline$.pipe(startWith(initial)))
  )
);

export const [useSaveEditedTimeline] = bind(
  editedTimeline$.pipe(
    switchMap((editedTimeline) =>
      saveEditedTimeline$.pipe(
        withLatestFrom(logbookFormState$),
        tap(([, state]) => {
          setLogbookFormState({
            ...state,
            time: convertFromNminTimeline(
              editedTimeline,
              LOBOOK_EDIT_INTERVAL_MINUTES
            ),
          });
        }),
        switchMap(() => NEVER)
      )
    )
  ),
  null
);

export const [useTimelineTotals] = bind(
  editedTimeline$.pipe(
    map((timeline) =>
      timeline.reduce(
        (acc, val) => ({
          ...acc,
          [val]: acc[val] + 1,
        }),
        { DRIVING: 0, RESTING: 0, WORKING: 0 } as Record<DriverStatus, number>
      )
    ),
    map((totals) =>
      mapValues(totals, (total) =>
        minutesToTimeString(total * LOBOOK_EDIT_INTERVAL_MINUTES)
      )
    )
  )
);

const uploadFile = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return request<LogbookSignatureReposponse>(
    '/gateway/logbook-service/logbook/file',
    {
      method: 'POST',
      body: formData,
    }
  );
};

export const [saveReportForLater$, saveReportForLater] = createSignal();
export const [submitLogbook$, submitLogbook] = createSignal();
export const [useSaveReportForLater] = bind(
  mergeWithKey({ saveReportForLater$, submitLogbook$ }).pipe(
    withLatestFrom(logbookFormState$, logbookReport$),
    switchMap(([{ type }, state, initialLogbook]) => {
      const hasId = 'id' in initialLogbook;
      const isSubmit = type === 'submitLogbook$';

      return (
        isSubmit
          ? uploadFile(state.signature!)
          : of<LogbookSignatureReposponse | null>(null)
      ).pipe(
        switchMap((signature) =>
          request('/gateway/logbook-service/logbook', {
            method: hasId ? 'PUT' : 'POST',
            body: JSON.stringify({
              ...state,
              distance: state.endKm - state.startKm,
              status: isSubmit ? 'SIGNED' : 'DRAFT',
              signature: signature?.fileName,
              ...(hasId ? { id: initialLogbook.id } : {}),
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          }).pipe(
            map(() => true),
            convertToAsyncTaskState(),
            tap(({ error, result }) => {
              if (error) {
                queueNotice({
                  variant: 'error',
                  timer: 3000,
                  message: 'Loogbook.SaveError',
                });
              }
              if (result) {
                navigate('/logbook');
              }
            })
          )
        )
      );
    })
  ),
  { running: false, error: null, result: null }
);
